export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Dental Anatomy, Oral Histology & Tooth Morphology',
    subtitle: 'The Foundation Every Dental Student Needs',
    intro: [
      'Every new class starts with the same challenge: 32 permanent teeth, 20 primary teeth, three numbering systems and dozens of structures that all look alike in a textbook.',
      'Tooth morphology, histology and the periodontium are hard to teach with words and slides full of text.',
      'This volume turns dental anatomy into labeled cross-sections, tooth-by-tooth morphology cards, numbering charts, eruption timelines, ❌/✅ comparisons and recall drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The oral cavity & dentitions at a glance',
      'Universal, FDI & Palmer numbering systems',
      'Tooth surfaces & directional terms',
      'Tooth structure cross-section',
      'Enamel, dentin & pulp histology',
      'Cementum, PDL & alveolar bone',
      'Oral mucosa & gingival anatomy',
      'Tooth development & eruption timelines',
      'Incisors, canines & premolars',
      'Maxillary & mandibular molars',
      'Primary teeth morphology',
      'Pulp chambers & canal configurations',
      'Occlusion & Angle classification',
      'TMJ, muscles of mastication & nerve supply',
      'Tooth identification method & recall games',
      '4-week dental anatomy plan & rubric',
    ],
    result: 'Give every student a clear, confident command of dental anatomy before their first preclinical session.',
  },
  {
    number: 'Volume 02',
    title: 'Oral Pathology, Dental Radiology & Clinical Diagnosis',
    subtitle: 'See It, Read It, Diagnose It',
    intro: [
      'Diagnosis is where students feel most unsure: lesions that look alike, radiographs full of shadows and a clinical exam with too many steps to remember.',
      'Without a systematic method, students confuse normal anatomy with pathology and miss what really matters.',
      'This volume organizes diagnosis and radiology into step-by-step exam sequences, lesion description guides, labeled radiographic anatomy, technique-error comparisons and case-based drills.',
    ],
    popular: true,
    popularLabel: 'CORE DIAGNOSIS',
    bulletsIntro: 'Inside:',
    bullets: [
      'The diagnostic process & medical history',
      'Extraoral & intraoral examination',
      'Oral cancer screening',
      'Hard tissue examination & charting',
      'Caries process, classification & risk',
      'Tooth wear & non-carious lesions',
      'Pulp & periapical tests',
      'Periodontal conditions, staging & grading',
      'Common oral soft tissue lesions',
      'Normal variants vs. pathology',
      'Radiation safety & ALARA',
      'Bitewing, periapical & panoramic images',
      'Normal radiographic anatomy',
      'Systematic radiograph reading',
      'Common technique errors',
      'Differential diagnosis & treatment planning',
    ],
    result: 'Help students examine, read radiographs and reach a diagnosis with a clear, repeatable method.',
  },
  {
    number: 'Volume 03',
    title: 'Restorative Dentistry, Dental Materials & Operative Techniques',
    subtitle: 'From the First Preparation to the Final Polish',
    intro: [
      'Operative dentistry is precise work: grasps, fulcrums, preparation walls, depths and material steps that students must get right every time.',
      'Explaining an outline form or a bonding sequence is hard until students can see every step side by side.',
      'This volume covers restorative dentistry with numbered preparation sequences, instrument charts, material comparison tables, ❌/✅ error cards and typodont lab drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Ergonomics & operator positioning',
      'Instrument grasps & fulcrums',
      'Hand & rotary instruments',
      'Isolation & rubber dam placement',
      'Cavity preparation terminology',
      'Class I to Class V preparations',
      'Caries removal & pulp protection',
      'Matrix systems',
      'Resin composites & adhesion',
      'Etching & bonding step by step',
      'Light-curing, finishing & polishing',
      'Glass ionomers & dental cements',
      'Shade selection & anterior esthetics',
      'Sealants & temporary restorations',
      'Evaluating a restoration & common errors',
      'Preclinical typodont progression & rubric',
    ],
    result: 'Help students prepare and restore teeth with precise, consistent technique.',
  },
  {
    number: 'Volume 04',
    title: 'Endodontics, Periodontics & Clinical Treatment Procedures',
    subtitle: 'Clinical Procedures, Step by Step',
    intro: [
      'Endodontic and periodontal procedures involve many steps, instruments and measurements that students must perform in the right order.',
      'Access, working length, probing depths and instrument selection are hard to teach until students can see each step clearly.',
      'This volume breaks down endodontic and periodontal treatment with labeled procedure diagrams, instrument guides, charting sheets, clinical workflows and simulation drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Pulp & periapical diagnosis review',
      'Root canal anatomy & instruments',
      'Access cavity preparation & locating canals',
      'Working length determination',
      'Cleaning, shaping & irrigation principles',
      'Obturation & radiographic evaluation',
      'Endodontic complications & emergencies',
      'Dental trauma basics',
      'Etiology of periodontal disease',
      'Periodontal probing technique & charting',
      'Mobility & furcation grading',
      'Scaling & root planing technique',
      'Gracey curette selection & sharpening',
      'Oral hygiene instruction & maintenance',
      'Clinical workflows: perio & root canal visits',
      'Simulation drills & 4-week plan',
    ],
    result: 'Help students perform endodontic and periodontal procedures with structure and confidence.',
  },
  {
    number: 'Volume 05',
    title: 'Oral Surgery, Local Anesthesia & Patient Management',
    subtitle: 'Safe Technique, Calm Patients',
    intro: [
      'Local anesthesia and oral surgery are where safety matters most, and where students are most nervous.',
      'Landmarks, injection techniques, instruments and complication management are hard to teach without clear visuals and safety checklists.',
      'This volume explains anesthesia, extractions and patient management with labeled landmark diagrams, step-by-step techniques, instrument charts, safety checklists and simulation drills.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Patient assessment before surgery',
      'Pain & anxiety management',
      'How local anesthesia works',
      'Local anesthetic agents & maximum doses',
      'The aspirating syringe & armamentarium',
      'Maxillary infiltration & nerve blocks',
      'Inferior alveolar nerve block',
      'Anesthesia complications & systemic reactions',
      'Medical emergencies in the dental office',
      'Oral surgery instruments',
      'Simple extraction step by step',
      'Forceps movements & elevator principles',
      'Suturing basics & hemostasis',
      'Post-operative care & complications',
      'Managing the anxious patient',
      'Suturing lab & emergency simulation drills',
    ],
    result: 'Help students anesthetize, extract and manage patients safely and confidently.',
  },
  {
    number: 'Volume 06',
    title: 'Dental Clinical Instruction, Teaching Programs & Practical Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to treat patients is not the same as knowing how to teach it.',
      'Dental instructors need clear methods for lectures, demonstrations, simulation labs, chairside teaching, feedback and competency assessment.',
      'This volume gives you lesson structures, demonstration guides, sample programs, simulation exercises, competency checklists, rubrics and progress trackers ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The role of the clinical dental instructor',
      'How adults learn clinical skills',
      'Writing learning objectives',
      'Designing lectures, labs & clinical sessions',
      'The 5-step clinical demonstration method',
      'Ready demonstration guides',
      'Simulation lab organization & exercises',
      'Case-based & flipped learning',
      'Giving effective feedback',
      'Chairside teaching & supervision',
      'Sample 12-week preclinical program',
      'Dental assisting & hygiene course design',
      'Competency-based education',
      'Competency checklists',
      'Practical assessment design & fair rubrics',
      'Tracking progress & supporting students',
    ],
    result: 'Plan, deliver and assess every lecture, lab and clinical session with structure, from first-year students to clinical rotations.',
  },
]
